import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@/contexts/user-provider";
import { parseError } from "@/lib/parse-error";
import { userIsAdmin } from "@/lib/permissions";
import { useIsSandboxTenant } from "@/lib/tenant-utils";
import { toast } from "@/lib/use-toast";
import type { TrainingItem, TrainingPersona, TrainingResponse } from "@/types/training";
import { changeTrainingPersona, completeTrainingItem, completeTrainingTour, fetchTeamTraining, fetchTraining, restartTraining, setTrainingExtraPersonas, skipTrainingItem, startTraining, uncompleteTrainingItem, unskipTrainingItem } from "./training";
export const trainingQueryKeys = {
  all: ["training"] as const,
  mine: () => [...trainingQueryKeys.all, "mine"] as const,
  team: () => [...trainingQueryKeys.all, "team"] as const
};

/** While a plan has open tasks, check every so often for work done elsewhere (card payments settle later). */
const OPEN_PLAN_REFETCH_MS = 30_000;
export function useTraining() {
  const $ = _c(3);
  const {
    currentUser
  } = useUser();
  const isSandbox = useIsSandboxTenant();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = trainingQueryKeys.mine();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = !!currentUser && isSandbox;
  let t2;
  if ($[1] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: fetchTraining,
      enabled: t1,
      staleTime: 10000,
      refetchInterval: _temp
    };
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return useQuery(t2);
}

/** True once the sandbox has training and this person has picked a role. */
function _temp(query) {
  const data = query.state.data;
  return data?.plan && data.totals.open > 0 ? OPEN_PLAN_REFETCH_MS : false;
}
export function useHasTrainingPlan() {
  const {
    data
  } = useTraining();
  return !!data?.available && !!data.plan;
}

/** The caller's plan still has tasks that are neither done nor skipped. */
export function useHasUnfinishedTraining() {
  const $ = _c(2);
  const {
    data
  } = useTraining();
  let t0;
  if ($[0] !== data) {
    t0 = !!data?.available && !!data.plan && data.items.some(_temp2);
    $[0] = data;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function _temp2(item) {
  return item.status !== "DONE" && item.status !== "SKIPPED";
}
export function useTeamTraining() {
  const $ = _c(6);
  const {
    currentUser
  } = useUser();
  const {
    data
  } = useTraining();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = trainingQueryKeys.team();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== currentUser || $[2] !== data?.available) {
    t1 = !!data?.available && userIsAdmin(currentUser);
    $[1] = currentUser;
    $[2] = data?.available;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: fetchTeamTraining,
      enabled: t1
    };
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return useQuery(t2);
}
async function toastFailure(error: unknown) {
  toast({
    title: "Could not update your training",
    description: await parseError(error),
    variant: "error"
  });
}
function usePlanMutation(mutationFn) {
  const $ = _c(5);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = data => {
      queryClient.setQueryData(trainingQueryKeys.mine(), data);
      queryClient.invalidateQueries({
        queryKey: trainingQueryKeys.team()
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== mutationFn || $[3] !== t0) {
    t1 = {
      mutationKey: trainingQueryKeys.all,
      mutationFn,
      onSuccess: t0,
      onError: toastFailure
    };
    $[2] = mutationFn;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
}
export const useStartTraining = () => {
  return usePlanMutation(_temp3);
};
export const useChangeTrainingPersona = () => {
  return usePlanMutation(_temp4);
};
export const useSetTrainingExtraPersonas = () => {
  return usePlanMutation(_temp5);
};
export const useRestartTraining = () => {
  return usePlanMutation(_temp6);
};
export const useCompleteTrainingTour = () => {
  return usePlanMutation(_temp7);
};

/** Recounts totals the same way the server does. */
function withItem(data: TrainingResponse, item: TrainingItem): TrainingResponse {
  const items = data.items.map(existing => existing.id === item.id ? item : existing);
  return {
    ...data,
    items,
    totals: {
      open: items.filter(i => i.status === "OPEN").length,
      done: items.filter(i => i.status === "DONE").length,
      skipped: items.filter(i => i.status === "SKIPPED").length
    }
  };
}
function useItemTransition(mutationFn) {
  const $ = _c(5);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = item => {
      queryClient.setQueryData(trainingQueryKeys.mine(), data => data ? withItem(data, item) : data);
      queryClient.invalidateQueries({
        queryKey: trainingQueryKeys.team()
      });
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== mutationFn || $[3] !== t0) {
    t1 = {
      mutationKey: trainingQueryKeys.all,
      mutationFn,
      onSuccess: t0,
      onError: toastFailure
    };
    $[2] = mutationFn;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
}
export const useCompleteTrainingItem = () => {
  return useItemTransition(completeTrainingItem);
};
export const useUncompleteTrainingItem = () => {
  return useItemTransition(uncompleteTrainingItem);
};
export const useSkipTrainingItem = () => {
  return useItemTransition(skipTrainingItem);
};
export const useUnskipTrainingItem = () => {
  return useItemTransition(unskipTrainingItem);
};
function _temp3(t0) {
  const {
    persona,
    extraPersonas: t1
  } = t0;
  const extraPersonas = t1 === undefined ? [] : t1;
  return startTraining(persona, extraPersonas);
}
function _temp4(t0) {
  const {
    persona,
    extraPersonas
  } = t0;
  return changeTrainingPersona(persona, extraPersonas);
}
function _temp5(extraPersonas) {
  return setTrainingExtraPersonas(extraPersonas);
}
function _temp6(_) {
  return restartTraining();
}
function _temp7(tour) {
  return completeTrainingTour(tour);
}